/**
 * The design system's own <Icon/> and <Whorl/> as plain DOM for the map markers (owner B5).
 *
 * React renders them once (never per frame): every icon into a detached node whose markup is cached,
 * and the Whorl into the site mark, after which the markers drive its ring opacities directly (the intro
 * fills it with `progress`). Roots are unmounted on a later task, never inside a React commit.
 */
import { useLayoutEffect, type JSX, type ReactNode } from "react";
import { createRoot, type Root } from "react-dom/client";
import { ICON_NAMES, Icon, Whorl, isIconName } from "@/design";

function Committed({ onCommit, children }: { onCommit: () => void; children: ReactNode }): JSX.Element {
  useLayoutEffect(() => {
    onCommit();
  }, [onCommit]);
  return <>{children}</>;
}

const unmountLater = (root: Root): void => {
  setTimeout(() => {
    try {
      root.unmount();
    } catch {
      /* already gone */
    }
  }, 0);
};

/* ─── Icons ─────────────────────────────────────────────────────────────── */

let icons: Map<string, string> | null = null;
let iconsPending: Promise<Map<string, string>> | null = null;

/** Resolves with the markup of every icon (rendered at 16 px; callers set width/height). */
export function loadIcons(): Promise<Map<string, string>> {
  if (icons) return Promise.resolve(icons);
  if (iconsPending) return iconsPending;
  iconsPending = new Promise((resolve) => {
    const host = document.createElement("div");
    const root = createRoot(host);
    const onCommit = (): void => {
      const m = new Map<string, string>();
      for (const el of host.querySelectorAll<HTMLElement>("[data-icon]")) m.set(el.dataset.icon ?? "", el.innerHTML);
      icons = m;
      resolve(m);
      unmountLater(root);
    };
    root.render(
      <Committed onCommit={onCommit}>
        {ICON_NAMES.map((n) => (
          <span key={n} data-icon={n}>
            <Icon name={n} size={16} />
          </span>
        ))}
      </Committed>,
    );
  });
  return iconsPending;
}

/** Cached icon markup (null until loadIcons() has resolved). Unknown names draw the map pin. */
export function iconMarkup(name: string): string | null {
  if (!icons) return null;
  return icons.get(isIconName(name) ? name : "mapPin") ?? null;
}

/** Puts an icon into `slot` at `size` px. */
export function setIcon(slot: HTMLElement, name: string, size: number): boolean {
  const html = iconMarkup(name);
  if (html === null) return false;
  if (slot.dataset.icon !== name) {
    slot.innerHTML = html;
    slot.dataset.icon = name;
  }
  const svg = slot.firstElementChild;
  if (svg) {
    svg.setAttribute("width", String(size));
    svg.setAttribute("height", String(size));
  }
  return true;
}

/* ─── The Whorl ─────────────────────────────────────────────────────────── */

export interface WhorlHandle {
  /** 0…1: the rings fill from the centre outwards (the Whorl's own rule, written to the DOM). */
  setProgress(p: number): void;
  setSize(px: number): void;
  destroy(): void;
}

export function mountWhorl(host: HTMLElement, size: number): WhorlHandle {
  const root = createRoot(host);
  let paths: SVGPathElement[] = [];
  let rings = 0;
  let progress = 1;
  let px = size;
  let dead = false;
  const last: number[] = [];

  const apply = (): void => {
    if (!rings) return;
    for (let k = 0; k < paths.length; k++) {
      const i = k % rings;
      const v = Math.round(Math.min(1, Math.max(0, progress * (rings + 2) - i)) * 1000) / 1000;
      if (last[k] !== v) {
        last[k] = v;
        paths[k].setAttribute("opacity", String(v));
      }
    }
  };
  const onCommit = (): void => {
    if (dead) return;
    const svg = host.querySelector("svg");
    if (!svg) return;
    // The two halves (upper, lower) each hold one path per ring, inside the clipped group.
    paths = Array.from(svg.querySelectorAll<SVGPathElement>(":scope > g > g > path"));
    rings = paths.length / 2;
    svg.setAttribute("width", String(px));
    svg.setAttribute("height", String(px));
    last.length = 0;
    apply();
  };
  root.render(
    <Committed onCommit={onCommit}>
      <Whorl size={size} progress={1} decorative />
    </Committed>,
  );
  return {
    setProgress(p: number): void {
      progress = Math.min(1, Math.max(0, p));
      apply();
    },
    setSize(s: number): void {
      px = s;
      const svg = host.querySelector("svg");
      if (svg) {
        svg.setAttribute("width", String(s));
        svg.setAttribute("height", String(s));
      }
    },
    destroy(): void {
      if (dead) return;
      dead = true;
      unmountLater(root);
    },
  };
}
